import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import { listGiftCardsForUser } from "../services/giftCards";
import { Gift, Copy, CheckCircle2, ArrowLeft, Lock, Mail } from "lucide-react";

const statusInfo = {
  active: { label: "AKTİV", cls: "text-[var(--primary)] border-[var(--primary)]" },
  redeemed: { label: "İŞLƏNİB", cls: "text-[var(--text-muted)] border-[var(--text-muted)]" },
  disabled: { label: "DAYANDIRILIB", cls: "text-[var(--accent)] border-[var(--accent)]" },
};

export default function MyGiftCardsPage() {
  const { user, profile, authChecked } = useAuth();
  const { t, formatPrice } = useApp();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (!user) return;
    listGiftCardsForUser(user.uid, profile?.email || user.email)
      .then((items) => {
        items.sort((a, b) => {
          const da = a.created_at?.toDate ? a.created_at.toDate().getTime() : 0;
          const db = b.created_at?.toDate ? b.created_at.toDate().getTime() : 0;
          return db - da;
        });
        setCards(items);
        setLoading(false);
      });
  }, [user, profile]);

  if (!authChecked) return <div className="min-h-[60vh] flex items-center justify-center text-[var(--text-2)]">{t.common.loading}</div>;
  if (!user) return <Navigate to="/login?next=/my-gift-cards" replace />;

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* ignore */ }
  };

  const purchased = cards.filter((c) => c.purchaser_user_id === user.uid);
  const received = cards.filter((c) => c.purchaser_user_id !== user.uid);

  const Card = ({ gc, mode }) => {
    const st = statusInfo[gc.status] || { label: gc.status?.toUpperCase(), cls: "text-[var(--text-muted)] border-[var(--text-muted)]" };
    const usable = gc.status === "active" && (gc.balance_azn || 0) > 0;
    return (
      <div className="border border-[var(--border)] hover:border-[var(--primary)] transition-colors overflow-hidden" data-testid={`my-gift-${gc.id}`}>
        <div className="aspect-[16/7] bg-gradient-to-br from-[var(--surface)] via-[var(--bg)] to-[var(--surface)] relative overflow-hidden p-6 flex justify-between items-start">
          <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_30%_30%,var(--accent),transparent_55%)]" />
          <div className="absolute inset-0 opacity-15 bg-[radial-gradient(circle_at_75%_75%,var(--primary),transparent_55%)]" />
          <div className="relative">
            <div className="metadata text-[var(--text-muted)]">{mode === "purchased" ? "ALINMIŞ" : "QƏBUL EDİLMİŞ"}</div>
            <div className="font-display text-2xl mt-1">{gc.name || "Hədiyyə Kartı"}</div>
          </div>
          <div className="relative text-right">
            <Gift size={20} className="ml-auto text-[var(--primary)]" />
            <div className={`metadata mt-2 border px-2 py-0.5 inline-block ${st.cls}`}>{st.label}</div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="metadata">BALANS</div>
              <div className="font-display text-3xl text-[var(--primary)]">{formatPrice(Number(gc.balance_azn) || 0)}</div>
            </div>
            <div className="text-right">
              <div className="metadata">İLKİN MƏBLƏĞ</div>
              <div className="font-mono">{formatPrice(Number(gc.initial_amount_azn) || 0)}</div>
            </div>
          </div>

          <div className="border border-[var(--border-soft)] bg-[var(--surface)] px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="metadata text-[var(--text-muted)]">KOD</div>
              <div className="font-mono text-sm text-[var(--primary)] truncate" data-testid={`gift-code-${gc.id}`}>{gc.code}</div>
            </div>
            <button
              onClick={() => copy(gc.code)}
              className="flex items-center gap-1 metadata text-[var(--text-2)] hover:text-[var(--primary)] flex-shrink-0"
              title="Kopyala"
              disabled={!usable}
            >
              {copied === gc.code ? <CheckCircle2 size={14} className="text-[var(--primary)]" /> : <Copy size={14} />}
              <span>{copied === gc.code ? "KOPYALANDI" : "KOPYALA"}</span>
            </button>
          </div>

          <div className="metadata text-[var(--text-muted)] flex flex-wrap gap-x-4 gap-y-1">
            <span>{gc.multi_use ? "ÇOXLU İSTİFADƏ" : "TƏK İSTİFADƏ"}</span>
            {mode === "purchased" && gc.recipient_name && <span>→ {gc.recipient_name}</span>}
            {mode === "purchased" && gc.recipient_email && <span className="normal-case tracking-normal text-[10px]">{gc.recipient_email}</span>}
            {mode === "received" && gc.purchaser_email && <span className="normal-case tracking-normal text-[10px]">göndərən: {gc.purchaser_email}</span>}
          </div>

          {gc.gift_message && (
            <div className="border-l-2 border-[var(--accent)] pl-3 italic text-sm text-[var(--text-2)]">
              "{gc.gift_message}"
            </div>
          )}

          {!usable && (
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
              <Lock size={12} />
              {gc.status === "redeemed" ? "Bu kart artıq tamamilə istifadə edilib." : "Bu kart hazırda istifadəyə uyğun deyil."}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="my-gift-cards-page">
      <Link to="/profile" className="metadata text-[var(--text-2)] hover:text-[var(--primary)] flex items-center gap-2"><ArrowLeft size={12} /> PROFİL</Link>
      <h1 className="section-title text-5xl md:text-7xl mt-4 flex items-center gap-4">
        <Gift size={48} className="text-[var(--primary)]" />
        Hədiyyə Kartlarım
      </h1>
      <p className="text-[var(--text-2)] mt-4 max-w-2xl">Aldığınız və sizə hədiyyə edilmiş bütün kartlar burada. Ödəniş zamanı kodu istifadə edin.</p>

      {loading ? (
        <div className="mt-16 text-center text-[var(--text-2)]">{t.common.loading}</div>
      ) : (
        <>
          {purchased.length > 0 && (
            <section className="mt-12">
              <div className="metadata mb-5 text-[var(--text-2)]">ALDIĞIM KARTLAR ({purchased.length})</div>
              <div className="grid md:grid-cols-2 gap-6">
                {purchased.map((gc) => <Card key={gc.id} gc={gc} mode="purchased" />)}
              </div>
            </section>
          )}

          {received.length > 0 && (
            <section className="mt-16">
              <div className="metadata mb-5 text-[var(--text-2)] flex items-center gap-2"><Mail size={12} /> MƏNƏ HƏDİYYƏ EDİLƏNLƏR ({received.length})</div>
              <div className="grid md:grid-cols-2 gap-6">
                {received.map((gc) => <Card key={gc.id} gc={gc} mode="received" />)}
              </div>
            </section>
          )}

          {!purchased.length && !received.length && (
            <div className="mt-16 border border-[var(--border)] p-16 text-center">
              <Gift size={36} className="text-[var(--text-muted)] mx-auto" />
              <div className="metadata mt-4 text-[var(--text-muted)]">SİZİN ÜÇÜN HƏLƏ HEÇ BİR HƏDİYYƏ KARTI YOXDUR</div>
              <Link to="/gift-cards" className="btn-primary mt-6 inline-flex">HƏDİYYƏ KARTI AL</Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
